'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { AuthLayout, FormInput, AlertMessage, SubmitButton } from '@/components/auth';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error } = await signIn(email, password);

    if (error) {
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('AuthRetryableFetchError')) {
        setError(
          'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.'
        );
      } else {
        setError(error?.message || 'Invalid email or password');
      }
      setLoading(false);
    } else {
      router.push('/start');
    }
  };

  const fillDemoCredentials = (type: 'demo' | 'admin') => {
    if (type === 'demo') {
      setEmail('demo@example.com');
      setPassword('demo123');
    } else {
      setEmail('admin@example.com');
      setPassword('admin123');
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your account to continue">
      <AlertMessage type="error" message={error} />

      <form onSubmit={handleSubmit} className="space-y-5">
        <FormInput
          id="email"
          label="Email Address"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />

        <FormInput
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="••••••••"
          required
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="remember"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
            />
            <label htmlFor="remember" className="ml-2 text-sm text-gray-600">
              Remember me
            </label>
          </div>
          <Link href="/forgot-password" className="text-sm text-violet-600 hover:text-violet-700 font-medium">
            Forgot password?
          </Link>
        </div>

        <SubmitButton loading={loading} loadingText="Signing in...">
          Sign In
        </SubmitButton>
      </form>

      {/* Sign Up Link */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          Don&apos;t have an account?{' '}
          <Link href="/signup" className="text-violet-600 hover:text-violet-700 font-semibold">
            Create an account
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

function DemoCredentialRow({
  label,
  credentials,
  onUse,
}: {
  label: string;
  credentials: string;
  onUse: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm font-medium text-gray-700">{label}</div>
        <div className="text-xs text-gray-500 font-mono mt-0.5">{credentials}</div>
      </div>
      <button
        type="button"
        onClick={onUse}
        className="text-xs font-medium bg-violet-100 hover:bg-violet-200 text-violet-700 px-3 py-1.5 rounded-lg transition-colors"
      >
        Use
      </button>
    </div>
  );
}
